import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PublicHeader } from "@/components/PublicHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Search, XCircle, Award } from "lucide-react";

export default function ValidateCertificate() {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(codeParam || "");
  const [state, setState] = useState("idle"); // idle | loading | ok | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const check = async (value) => {
    const c = (value ?? code).trim();
    if (!c) return;
    setState("loading");
    setError("");
    try {
      const res = await api.get(`/public/certificate/${encodeURIComponent(c)}`);
      setResult(res.data);
      setState("ok");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      setState("error");
    }
  };

  useEffect(() => {
    if (codeParam) check(codeParam);
    // eslint-disable-next-line
  }, [codeParam]);

  const dateStr = result ? new Date(result.issued_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicHeader />
      <main className="max-w-xl mx-auto px-5 py-16">
        <div className="text-center mb-8">
          <div className="h-14 w-14 mx-auto rounded-2xl grid place-items-center text-white mb-4" style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}>
            <ShieldCheck className="h-7 w-7" />
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Validar certificado</h1>
          <p className="text-slate-500 mt-2">Digite o código impresso no certificado para conferir a autenticidade.</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="space-y-1.5">
            <Label>Código de validação</Label>
            <div className="flex gap-2">
              <Input
                data-testid="validate-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && check()}
                placeholder="IEP-XXXXXXXX"
                className="font-mono uppercase"
              />
              <Button data-testid="validate-submit-button" onClick={() => check()} disabled={state === "loading"} className="text-white shrink-0" style={{ background: "#232D4F" }}>
                {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {state === "ok" && result && (
            <div data-testid="validate-result-ok" className="mt-6 rounded-xl border border-teal-100 bg-teal-50/60 p-5 animate-fade-up">
              <div className={`flex items-center gap-2 font-semibold ${result.revoked ? "text-red-700" : result.expired ? "text-amber-700" : "text-teal-700"}`}><Award className="h-5 w-5" /> {result.revoked ? "Certificado cancelado" : result.expired ? "Certificado autêntico, porém vencido" : "Certificado válido"}</div>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Aluno</dt><dd className="font-medium text-slate-800 text-right">{result.student_name}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Curso</dt><dd className="font-medium text-slate-800 text-right">{result.course_title}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Emitido em</dt><dd className="font-medium text-slate-800 text-right">{dateStr}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Validade</dt><dd className="font-medium text-slate-800 text-right">{result.valid_until ? `até ${result.valid_until.split("-").reverse().join("/")}` : "Vitalícia"}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Código</dt><dd className="font-mono text-slate-800 text-right">{result.code}</dd></div>
              </dl>
            </div>
          )}

          {state === "error" && (
            <div data-testid="validate-result-error" className="mt-6 rounded-xl border border-red-100 bg-red-50/60 p-5 flex items-center gap-3 text-red-700">
              <XCircle className="h-5 w-5 shrink-0" /> <span className="text-sm">{error}</span>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
